// src/observer/bgIgnition.ts
// Bridges the pure background decision (resolver) to the GSAP igniter.
// GSAP-only. Gate/ungate is owned by renderFirstSection/audioGate now.

import { resolveBackgroundPosition, type BgDecision } from "../utils/backgroundPositionResolver";
import { igniteBackground } from "../utils/backgroundGsap";

const PENDING = Symbol("bgIgnitionTimer");

type BgIgniteOpts = {
  /** Fraction of height that must be visible (0..1). Default 0.6 */
  threshold?: number;
  /** Delay in ms before attempting ignition. Default 250 */
  delayMs?: number;
  /** Extra pixels considered visible above/below viewport (root margin). Default 24 */
  marginPx?: number;
  /** Honor prefers-reduced-motion by skipping ignition (overridden if window.__SC_FORCE_MOTION__). Default true */
  honorReducedMotion?: boolean;
};

/** True if ≥ threshold of the element’s height is in the viewport (with optional margin). */
export function isElementSufficientlyVisible(
  el: HTMLElement,
  threshold = 0.6,
  marginPx = 24
): boolean {
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const top = -marginPx;
  const bottom = vh + marginPx;
  const visible = Math.max(0, Math.min(rect.bottom, bottom) - Math.max(rect.top, top));
  if (rect.height <= 0) return false;
  return visible / rect.height >= threshold;
}

/** Default delay (ms) before attempting to ignite a background once section becomes active. */
export function getBgStartDelayMs(): number {
  return 250;
}

/** Should we animate this section’s background at all? */
function shouldAnimateBackground(section: HTMLElement): boolean {
  // Skip if a video background is present — per “no bg anim on video” rule.
  const hasVideoBg = !!section.querySelector(".sc-video-frame, video.sc-media");
  if (hasVideoBg) return false;

  // Must have a sprite with a non-static anim
  const sprite = section.querySelector<HTMLElement>(".bg-anim-sprite");
  if (!sprite) return false;

  const kind = sprite.getAttribute("data-bg-anim") || "";
  if (!kind || kind === "static") return false;

  // Don’t re-ignite once done.
  if (section.dataset.bgIgnited === "1") return false;

  return true;
}

function prefersReducedMotion(): boolean {
  // DEV override: force motion even if OS says reduce
  if ((window as any).__SC_FORCE_MOTION__) return false;
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
  } catch {
    return false;
  }
}

function markIgnited(section: HTMLElement, state: "1" | "skipped" | "0" = "1") {
  section.dataset.bgIgnited = state;
}

/** Decide + ignite background GSAP for a given section element (when attrs are available in memory). */
export function igniteBackgroundFor(
  sectionEl: HTMLElement,
  attrs: Record<string, any>,
  screenIsVertical: boolean,
  source: "background-v" | "background-h" | null
) {
  try {
    const decision: BgDecision = resolveBackgroundPosition(attrs, screenIsVertical, source);
    console.debug("[bgIgnition] igniteBackgroundFor decision:", decision);
    return igniteBackground(sectionEl, decision);
  } catch (err) {
    console.warn("[bgIgnition] failed to ignite background:", err);
    return null;
  }
}

/** Infer everything from DOM (sprite data-attrs stamped by renderer) and ignite via GSAP. */
export function igniteBackgroundFromDom(sectionEl: HTMLElement) {
  try {
    // No CSS animation usage — the igniter reads data-* only.
    const tl = igniteBackground(sectionEl);
    return tl;
  } catch (err) {
    console.warn("[bgIgnition] DOM-only ignite failed:", err);
    return null;
  }
}

/** Schedule ignition after a small delay if the section is sufficiently visible. */
export function scheduleBackgroundIgnitionFor(
  section: HTMLElement,
  opts: BgIgniteOpts = {}
) {
  const {
    threshold = 0.6,
    delayMs = getBgStartDelayMs(),
    marginPx = 24,
    honorReducedMotion = true,
  } = opts;

  // clear any previous pending timer for this section
  const pending: number | undefined = (section as any)[PENDING as any];
  if (pending) window.clearTimeout(pending);

  const timer = window.setTimeout(() => {
    if (!section.isConnected) return;
    if (!shouldAnimateBackground(section)) return;

    if (honorReducedMotion && prefersReducedMotion()) {
      markIgnited(section, "skipped");
      return;
    }

    if (!isElementSufficientlyVisible(section, threshold, marginPx)) return;

    const idx = section.dataset.sectionIndex || "?";
    console.debug("[OBSERVER] Igniting background for section", idx);
    try {
      igniteBackgroundFromDom(section);
      markIgnited(section, "1");
    } catch (err) {
      console.warn("[OBSERVER] Background ignition failed:", err);
    }
  }, delayMs);

  (section as any)[PENDING as any] = timer;
}

/** Force immediate ignition (used for the first section after audio gate). */
export function forceBackgroundIgnition(section: HTMLElement) {
  const pending: number | undefined = (section as any)[PENDING as any];
  if (pending) window.clearTimeout(pending);

  if (!section.isConnected) return;
  if (!shouldAnimateBackground(section)) return;

  // Force = force; do not skip for reduced motion here.

  const idx = section.dataset.sectionIndex || "?";
  console.debug("[OBSERVER] Forcing background ignition for section", idx);
  try {
    igniteBackgroundFromDom(section);
    markIgnited(section, "1");
  } catch (err) {
    console.warn("[OBSERVER] Background ignition failed:", err);
  }
}

// Debug/interop hooks
try {
  (window as any).storycore = (window as any).storycore || {};
  (window as any).storycore.scheduleBackgroundIgnitionFor = scheduleBackgroundIgnitionFor;
  (window as any).storycore.forceBackgroundIgnition = forceBackgroundIgnition;
} catch {}